<script setup lang="ts">
  import { Routes } from '@/const/Routes'
  import type { SubjectContent } from '@/types/Subject'
  import { useAppRouting } from '@/composables/useAppRouting'

  import BorderList from '@/components/common/BorderList.vue'
  import Typography from '@/components/common/Typography.vue'

  const props = withDefaults(
    defineProps<{
      subjectContents: SubjectContent[]
      title?: string
    }>(),
    {
      title: 'Fagstoff'
    }
  )

  const { getRouteWithParams } = useAppRouting()

  const createNumericLabel = (index: number): string => {
    const label = index + 1
    return label < 10 ? `0${label}` : label.toString()
  }
</script>

<template>
  <Typography
    component="h2"
    variant="h3"
    data-testid="subject-content-list-title"
  >
    {{ props.title }}
  </Typography>
  <BorderList list-type="ol">
    <li
      v-for="(subjectContent, index) in props.subjectContents"
      :key="index"
      :data-testid="`subject-content-list-item-${index}`"
    >
      <router-link
        class="list-item"
        :to="getRouteWithParams(Routes.SubjectContent, subjectContent)"
      >
        <Typography
          aria-hidden="true"
          variant="number-small"
          class="list-item-label"
        >
          {{ createNumericLabel(index) }}
        </Typography>
        <div class="list-item-title-wrapper">
          <Typography
            variant="h4"
            component="h3"
            class="list-item-title"
          >
            {{ subjectContent.title }}
          </Typography>
        </div>
        <Typography variant="label">
          {{ subjectContent.category }}
        </Typography>
        <Typography
          v-if="subjectContent.shortDescription"
          class="short-description"
        >
          {{ subjectContent.shortDescription }}
        </Typography>
      </router-link>
    </li>
  </BorderList>
</template>

<style lang="scss" scoped>
  .list-item {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 16px 32px;
    color: var(--color-blue-50);
    line-height: 150%; /* 42px */
    text-decoration: none;
    transition: all .15s;
    justify-content: center;

    @include visible-focus-ring;

    &:focus-visible,
    &:hover {
      background-color: var(--color-yellow-20);
    }

    .short-description {
      margin: 12px 0 0;
      padding-right: 46px;
    }
  }

  .list-item-title {
    margin: 0;
  }

  .list-item-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
</style>
